import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

import { helperService } from '../../../services/helperService';

const AvailabilityManager = () => {
  const [activeTab, setActiveTab] = useState('recurring');
  const [recurringSchedule, setRecurringSchedule] = useState([
    { id: 'monday', day: 'monday', dayOfWeek: 1, enabled: false, slots: [] },
    { id: 'tuesday', day: 'tuesday', dayOfWeek: 2, enabled: false, slots: [] },
    { id: 'wednesday', day: 'wednesday', dayOfWeek: 3, enabled: false, slots: [] },
    { id: 'thursday', day: 'thursday', dayOfWeek: 4, enabled: false, slots: [] },
    { id: 'friday', day: 'friday', dayOfWeek: 5, enabled: false, slots: [] },
    { id: 'saturday', day: 'saturday', dayOfWeek: 6, enabled: false, slots: [] },
    { id: 'sunday', day: 'sunday', dayOfWeek: 0, enabled: false, slots: [] }
  ]);

  const [helperId, setHelperId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [newSlot, setNewSlot] = useState({
    dayId: '',
    startTime: '',
    duration: '60',
    sessionType: 'video'
  });

  const sessionTypeOptions = [
    { value: 'video', label: 'Video Call' },
    { value: 'audio', label: 'Audio Call' },
    { value: 'chat', label: 'Chat Only' }
  ];

  const durationOptions = [
    { value: '30', label: '30 minutes' },
    { value: '60', label: '60 minutes' },
    { value: '90', label: '90 minutes' }
  ];

  useEffect(() => {
    loadHelperData();
  }, []);

  const loadHelperData = async () => {
    setLoading(true);
    setError('');

    try {
      // Get current helper profile
      const { data: helperData, error: helperError } = await helperService?.getCurrentHelperProfile();
      
      if (helperError) {
        setError('Failed to load helper profile');
        return;
      }

      if (!helperData) {
        setError('Helper profile not found');
        return;
      }

      setHelperId(helperData?.id);

      // Load availability slots
      const { data: availabilityData, error: availError } = await helperService?.getHelperAvailability(helperData?.id);
      
      if (availError) {
        setError('Failed to load availability slots');
        return;
      }

      // Group slots by day of week
      const scheduleMap = {};
      availabilityData?.forEach(slot => {
        const dayOfWeek = slot?.dayOfWeek;
        if (!scheduleMap?.[dayOfWeek]) {
          scheduleMap[dayOfWeek] = [];
        }
        scheduleMap?.[dayOfWeek]?.push({
          id: slot?.id,
          startTime: slot?.timeSlot,
          duration: slot?.duration,
          sessionType: slot?.sessionType
        });
      });

      // Update recurring schedule with loaded data
      setRecurringSchedule(prev => prev?.map(day => ({
        ...day,
        enabled: scheduleMap?.[day?.dayOfWeek]?.length > 0,
        slots: scheduleMap?.[day?.dayOfWeek] || []
      })));

    } catch (err) {
      setError(err?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSlot = async () => {
    if (!newSlot?.dayId || !newSlot?.startTime) {
      setError('Please select a day and start time');
      return;
    }

    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const selectedDay = recurringSchedule?.find(d => d?.id === newSlot?.dayId);
      
      const { data, error: createError } = await helperService?.createAvailabilitySlot(helperId, {
        dayOfWeek: selectedDay?.dayOfWeek,
        timeSlot: newSlot?.startTime,
        duration: parseInt(newSlot?.duration),
        sessionType: newSlot?.sessionType,
        isAvailable: true
      });

      if (createError) {
        setError(createError?.message || 'Failed to add slot');
        return;
      }

      // Update local state
      setRecurringSchedule(prev => prev?.map(day => {
        if (day?.id === newSlot?.dayId) {
          return {
            ...day,
            enabled: true,
            slots: [...day?.slots, {
              id: data?.id,
              startTime: newSlot?.startTime,
              duration: parseInt(newSlot?.duration),
              sessionType: newSlot?.sessionType
            }]
          };
        }
        return day;
      }));

      setNewSlot({ dayId: '', startTime: '', duration: '60', sessionType: 'video' });
      setSuccessMessage('Slot added successfully');
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (err) {
      setError(err?.message || 'Failed to add slot');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSlot = async (dayId, slotId) => {
    if (!confirm('Are you sure you want to delete this slot?')) return;

    setSaving(true);
    setError('');

    try {
      const { error: deleteError } = await helperService?.deleteAvailabilitySlot(slotId);

      if (deleteError) {
        setError(deleteError?.message || 'Failed to delete slot');
        return;
      }

      // Update local state
      setRecurringSchedule(prev => prev?.map(day => {
        if (day?.id === dayId) {
          const updatedSlots = day?.slots?.filter(s => s?.id !== slotId);
          return {
            ...day,
            enabled: updatedSlots?.length > 0,
            slots: updatedSlots
          };
        }
        return day;
      }));

      setSuccessMessage('Slot deleted successfully');
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (err) {
      setError(err?.message || 'Failed to delete slot');
    } finally {
      setSaving(false);
    }
  };

  const toggleDayEnabled = (dayId) => {
    setRecurringSchedule(recurringSchedule?.map(day =>
      day?.id === dayId ? { ...day, enabled: !day?.enabled } : day
    ));
  };

  const formatDay = (day) => {
    return day?.charAt(0)?.toUpperCase() + day?.slice(1);
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time?.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading availability...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Calendar" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Availability Management</h2>
            <p className="text-sm text-muted-foreground">Set your recurring schedule and time slots</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-error/10 border border-error/20 rounded-lg flex items-start gap-3">
          <Icon name="AlertCircle" size={20} className="text-error mt-0.5" />
          <p className="text-sm text-error">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-4 bg-success/10 border border-success/20 rounded-lg flex items-start gap-3">
          <Icon name="CheckCircle2" size={20} className="text-success mt-0.5" />
          <p className="text-sm text-success">{successMessage}</p>
        </div>
      )}

      <div className="bg-muted/50 rounded-lg p-4 mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-4">Add Availability Slot</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Select
            label="Day"
            placeholder="Select day"
            options={recurringSchedule?.map(d => ({ value: d?.id, label: formatDay(d?.day) }))}
            value={newSlot?.dayId}
            onChange={(value) => setNewSlot({ ...newSlot, dayId: value })}
          />
          <Input
            label="Start Time"
            type="time"
            value={newSlot?.startTime}
            onChange={(e) => setNewSlot({ ...newSlot, startTime: e?.target?.value })}
          />
          <Select
            label="Duration"
            options={durationOptions}
            value={newSlot?.duration}
            onChange={(value) => setNewSlot({ ...newSlot, duration: value })}
          />
          <Select
            label="Session Type"
            options={sessionTypeOptions}
            value={newSlot?.sessionType}
            onChange={(value) => setNewSlot({ ...newSlot, sessionType: value })}
          />
          <div className="flex items-end">
            <Button
              variant="default"
              iconName="Plus"
              iconPosition="left"
              onClick={handleAddSlot}
              disabled={saving || !newSlot?.dayId || !newSlot?.startTime}
              className="w-full"
            >
              {saving ? 'Adding...' : 'Add Slot'}
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-primary mt-0.5" />
            <div>
              <p className="text-sm text-foreground font-medium">Recurring Schedule</p>
              <p className="text-sm text-muted-foreground mt-1">
                Your weekly availability pattern. These slots will repeat every week and automatically update across the platform.
              </p>
            </div>
          </div>
        </div>

        {recurringSchedule?.map((day) => (
          <div key={day?.id} className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-foreground min-w-[100px]">{formatDay(day?.day)}</span>
                {day?.slots?.length > 0 && (
                  <span className="text-xs text-muted-foreground">({day?.slots?.length} slot{day?.slots?.length !== 1 ? 's' : ''})</span>
                )}
              </div>
            </div>

            {day?.slots?.length > 0 ? (
              <div className="space-y-2">
                {day?.slots?.map((slot) => (
                  <div key={slot?.id} className="flex items-center justify-between bg-muted/30 rounded-md p-3">
                    <div className="flex items-center gap-3">
                      <Icon name="Clock" size={16} className="text-muted-foreground" />
                      <span className="text-sm text-foreground font-mono">
                        {formatTime(slot?.startTime)} ({slot?.duration} min)
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                        {slot?.sessionType}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Trash2"
                      onClick={() => handleDeleteSlot(day?.id, slot?.id)}
                      disabled={saving}
                      className="text-destructive"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground ml-3">No slots scheduled</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AvailabilityManager;